import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, formatDateToMonthDayYear, useT } from '@repo/common';
import WorkshopOptionList from './WorkshopOptionList';
import BookingDetailModal from './BookingDetailModal';
import WriteReviewModal from './WriteReviewModal';
import { getWorkshopReviews } from '../../../../api/workshop/workshopReview.api';

import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_more-vert.svg';

const LABEL_KR = {
  Waitlist: '대기',
  Upcoming: '예정',
  Attended: '참석',
  Absent: '불참',
};

const WorkshopRow = ({ item, onCancelClick }) => {
  const t = useT();
  const navigate = useNavigate();
  const [isOverCancel, setIsOverCancel] = useState(false);
  const [showOptionList, setShowOptionList] = useState(false);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const optionButtonRef = useRef(null);
  const imageUrl = item.imageUrl;
  const isWaitlist = item.status === 'WAITLISTED';
  const isAttended = item.status === 'ATTENDED';

  const label = (() => {
    if (isWaitlist) {
      return 'Waitlist';
    }
    if (isAttended) {
      return 'Attended';
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    if (item.date > todayStr) {
      return 'Upcoming';
    }

    return 'Absent';
  })();

  const showLabel = label === 'Attended' || label === 'Absent';
  const labelTone =
    label === 'Attended'
      ? { bg: 'bg-green-light02', text: 'text-green-deep04' }
      : { bg: 'bg-grayscale-gray02', text: 'text-text-placeholder' };

  // 버튼 문구를 "후기"/"후기 수정" 중 맞게 띄우려고 참석한 행만 확인한다
  useEffect(() => {
    if (!isAttended || !item.workshopId) return undefined;

    let active = true;
    getWorkshopReviews(item.workshopId)
      .then((data) => {
        if (active) setHasReviewed(Boolean(data?.myReview));
      })
      .catch(() => {
        // 못 불러와도 작성은 가능하다. 문구만 기본값으로 둔다
        if (active) setHasReviewed(false);
      });

    return () => {
      active = false;
    };
  }, [isAttended, item.workshopId]);

  const handleViewInvoiceClick = () => {
    setShowOptionList(false);
    navigate(`/mypage?tab=Payments&invoice=${item.invoiceNumber}`);
  };

  return (
    <div
      className={`flex items-center w-full tabletH:h-[118px] p-3 gap-2 tabletH:p-6 tabletH:gap-6 rounded-[15px] bg-grayscale-gray00 border-[1.6px] border-transparent cursor-pointer
      transition-all duration-200 
      ${
        !isOverCancel
          ? 'hover:border-surface-green40 active:bg-green-light01'
          : ''
      }`}
      onClick={(e) => {
        if (showOptionList) return;
        navigate(`/community/kpathport/workshop/${item.workshopId}`);
      }}
    >
      <img
        src={imageUrl}
        alt={t('default profile', '기본 프로필')}
        className='size-[52px] min-w-[52px] min-h-[52px] tabletH:size-[70px] tabletH:min-w-[70px] tabletH:min-h-[70px] rounded-lg object-cover'
      />

      <div className='flex flex-col gap-1 w-full min-w-0'>
        <div className='flex gap-1.5 tabletH:gap-2'>
          {showLabel && (
            <Chip
              type='fill'
              state='disabled'
              size='xs'
              hideIcon
              disableTypography
              disableDefaultColors
              bgclass={`h-auto rounded-md px-1 py-[1px] tabletH:px-1.5 tabletH:py-0.5 ${labelTone.bg}`}
              borderclass='ring-0'
              textclass={`typo-b11Strong tabletH:typo-b12Strong ${labelTone.text}`}
            >
              {t(label, LABEL_KR[label])}
            </Chip>
          )}
          <div className='flex gap-1 text-grayscale-gray05 typo-b12 tabletH:typo-b14Strong'>
            <div>{item.location} ·</div>
            <div>{formatDateToMonthDayYear(item.date)}</div>
          </div>
        </div>
        <div className='typo-b16Strong tabletH:typo-b20Strong text-grayscale-gray08 truncate'>
          {item.workshopTitle}
        </div>
      </div>

      {/* 참석 확인된 워크숍만 후기를 쓸 수 있다 */}
      {isAttended && (
        <div
          onMouseEnter={() => setIsOverCancel(true)}
          onMouseLeave={() => setIsOverCancel(false)}
          className='flex shrink-0 items-center justify-center'
        >
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              setShowReviewModal(true);
            }}
            className='whitespace-nowrap px-2 typo-b13Strong text-green-deep03 underline underline-offset-2 transition-colors hover:text-green-deep04'
          >
            {hasReviewed ? t('Edit review', '후기 수정') : t('Review', '후기')}
          </button>
        </div>
      )}

      <div
        onMouseEnter={() => setIsOverCancel(true)}
        onMouseLeave={() => setIsOverCancel(false)}
        className='flex items-center justify-center'
      >
        <button
          ref={optionButtonRef}
          onClick={(e) => {
            e.stopPropagation();
            const rect = optionButtonRef.current.getBoundingClientRect();
            setAnchorRect(rect);
            setShowOptionList(true);
          }}
        >
          <OptionIcon className='text-icon-secondary hover:text-icon-primary' />
        </button>
      </div>

      {showOptionList && (
        <WorkshopOptionList
          anchorRect={anchorRect}
          onClose={() => {
            setShowOptionList(false);
            setAnchorRect(null);
          }}
          onCancelClick={() => onCancelClick(item.bookingId, isWaitlist)}
          onViewInvoice={handleViewInvoiceClick}
          onViewBookingClick={() => {
            setShowOptionList(false);
            setAnchorRect(null);
            setShowBookingDetail(true);
          }}
          label={label}
          booking={item}
        />
      )}

      {showBookingDetail && (
        <BookingDetailModal
          data={item}
          label={label}
          onClose={() => setShowBookingDetail(false)}
        />
      )}

      {showReviewModal && (
        <WriteReviewModal
          isOpen
          workshopId={item.workshopId}
          workshopTitle={item.workshopTitle}
          onClose={() => setShowReviewModal(false)}
          // 버튼 문구를 바로 맞추기 위해 모달이 알려주는 상태를 받는다
          onSaved={(saved) => setHasReviewed(saved)}
        />
      )}
    </div>
  );
};

export default WorkshopRow;
